import React, { useState, useEffect, useRef, useId, useCallback } from "react";
import { createPortal } from "react-dom";
import api from "../../api.js";
import "../../CSS/DeliveryUpdate.css";

/* UI status -> status accepted by drs.DeliveryUpdate / drs.DRSConsignment (see drs_Appentry_test) */
const STATUS_TO_DB = {
  DELIVERED: "DELIVERED",
  PARTIAL: "PARTIAL",
  OUT_FOR_DELIVERY: "ATTEMPTED",
  UNDELIVERED: "ATTEMPTED",
  REFUSED: "ATTEMPTED",
  RTO: "RETURNED",
};


/* ============================================================
   CONFIG — override via props
   ============================================================ */
const DEFAULT_STATUSES = [
  { value: "DELIVERED", label: "Delivered" },
  { value: "OUT_FOR_DELIVERY", label: "Out for Delivery" },
  { value: "UNDELIVERED", label: "Undelivered" },
  { value: "PARTIAL", label: "Partially Delivered" },
  { value: "REFUSED", label: "Refused by Consignee" },
  { value: "RTO", label: "Return to Origin" },
];
const DEFAULT_REASONS = [
  "Consignee not available",
  "Premises closed / Door locked",
  "Incorrect / incomplete address",
  "Consignee refused to accept",
  "Payment / documents not ready",
  "Vehicle breakdown",
  "Other",
];
const DEFAULT_RECEIVED_AT = [
  "Main Gate / Security",
  "Stores / Warehouse",
  "Reception",
  "Site Office",
  "Consignee Premises",
];
const MAX_BYTES = 5 * 1024 * 1024;
// Same allow-list as MobileVersionController's AllowedPodExtensions — reject
// early here instead of after a full upload just to get a 400 back.
const ALLOWED_POD_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];

const EMPTY_FORM = {
  dest: "",
  time: "",
  status: "",
  receiver: "",
  receivedAt: "",
  mobile: "",
  pkgs: "",
  reason: "",
  remarks: "",
};

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

function nowLocal() {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

/* Which fields each status needs */
function rulesFor(status) {
  const receipt = status === "DELIVERED" || status === "PARTIAL";
  return {
    receiptRequired: receipt, // Receiver + Received At mandatory
    showMobile: receipt,
    showPod: receipt,
    showPkgs: status === "PARTIAL",
    showReason: ["UNDELIVERED", "REFUSED", "RTO", "PARTIAL"].includes(status),
  };
}

function badgeClass(status = "") {
  const s = status.toUpperCase();
  if (s === "DELIVERED") return "green";
  if (["IN TRANSIT", "OUT FOR DELIVERY", "PARTIALLY DELIVERED"].includes(s)) return "amber";
  if (["UNDELIVERED", "RTO", "RETURN TO ORIGIN", "REFUSED BY CONSIGNEE"].includes(s)) return "red";
  return "";
}

/* ============================================================
   Icons
   ============================================================ */
const BackIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <line x1="20" y1="12" x2="4" y2="12" />
    <polyline points="10 6 4 12 10 18" />
  </svg>
);
const SearchIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
    <circle cx="11" cy="11" r="7" />
    <line x1="21" y1="21" x2="16.5" y2="16.5" />
  </svg>
);
const ClockIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
    <circle cx="12" cy="12" r="9" />
    <polyline points="12 7 12 12 15 14" />
  </svg>
);
const CameraIcon = () => (
  <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
    <circle cx="12" cy="13" r="4" />
  </svg>
);

/* ============================================================
   Toast hook
   ============================================================ */
function useToast() {
  const [toast, setToast] = useState({ msg: "", type: "", show: false });
  const timer = useRef();
  const show = useCallback((msg, type = "") => {
    clearTimeout(timer.current);
    setToast({ msg, type, show: true });
    timer.current = setTimeout(() => setToast((t) => ({ ...t, show: false })), 2600);
  }, []);
  useEffect(() => () => clearTimeout(timer.current), []);
  return [toast, show];
}

/* ============================================================
   Small building blocks
   ============================================================ */
function Field({ name, label, required, error, full, fieldRef, children }) {
  return (
    <div ref={fieldRef} className={`dlv-field ${full ? "full" : ""} ${error ? "has-error" : ""}`} data-field={name}>
      <label htmlFor={`dlv-${name}`}>
        {label} {required && <span className="dlv-req">*</span>}
      </label>
      {children}
      {error && <div className="dlv-err">{error}</div>}
    </div>
  );
}

function Detail({ label, value, badge }) {
  return (
    <div className="dlv-detail">
      <dt>{label}</dt>
      {value ? (
        <dd>{badge ? <span className={`dlv-badge ${badgeClass(value)}`}>{value}</span> : value}</dd>
      ) : (
        <dd className="empty">----------</dd>
      )}
    </div>
  );
}

/* ============================================================
   Main screen
   ============================================================ */
export default function DeliveryUpdate({
  statuses = DEFAULT_STATUSES,
  reasons = DEFAULT_REASONS,
  receivedAtOptions = DEFAULT_RECEIVED_AT,
  companyCode = "", // company_code sent to drs-delivery-update (drs_Appentry_test)
  userId = "",      // guser_id sent to drs-delivery-update
  demo = false, // API is wired — pass demo={true} to force mock data
  onBack = () => window.history.back(),
  onUpdated, // (consignmentNo, statusLabel, response) => void
}) {
  const [cnNo, setCnNo] = useState("");
  const [cnInvalid, setCnInvalid] = useState(false);
  const [tracked, setTracked] = useState(null);
  const [tracking, setTracking] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [pod, setPod] = useState(null); // { file, url }
  const [submitting, setSubmitting] = useState(false);
  const [toast, notify] = useToast();

  const podInputRef = useRef(null);
  const fieldRefs = useRef({});
  const regField = (name) => (el) => (fieldRefs.current[name] = el);

  const rules = rulesFor(form.status);

  useEffect(() => () => pod && URL.revokeObjectURL(pod.url), [pod]);

  /* ----- Form helpers ----- */
  const setField = (name, value) => {
    setForm((f) => ({ ...f, [name]: value }));
    setErrors((e) => (e[name] ? { ...e, [name]: undefined } : e));
  };
  const bind = (name) => ({
    id: `dlv-${name}`,
    value: form[name],
    onChange: (e) => setField(name, e.target.value),
    className: `dlv-input ${errors[name] ? "invalid" : ""}`,
  });

  const handleStatus = (e) => {
    const status = e.target.value;
    setForm((f) => ({ ...f, status }));
    setErrors((er) => ({ ...er, status: undefined, receiver: undefined, receivedAt: undefined, pkgs: undefined, reason: undefined }));
    if (!rulesFor(status).showPod) clearPod();
  };

  /* ----- Track ----- */
  const fetchConsignment = async (cn) => {
    if (demo) {
      await sleep(600);
      if (cn.length < 4) throw new Error("Consignment not found");
      return {
        cnNo: cn,
        consignor: "Shree Transformers Pvt. Ltd., Kalyan",
        consignee: "Gujarat Power Solutions, Vadodara",
        status: "In Transit",
        alreadyDelivered: false,
        receivedBy: null,
        remarks: null,
        deliveredOn: null,
        podImageUrl: null,
        date: new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }),
        destination: "Vadodara",
      };
    }
    const res = await api.get(`/drs-track/${encodeURIComponent(cn)}`);
    if (!res.data?.success) throw new Error(res.data?.message || "Consignment not found");
    return res.data.data;
  };

  const handleTrack = async (e) => {
    e.preventDefault();
    const cn = cnNo.trim().toUpperCase();
    if (!cn) {
      setCnInvalid(true);
      notify("Enter consignment number", "bad");
      return;
    }
    setTracking(true);
    try {
      const data = await fetchConsignment(cn);
      setTracked(data);
      if (data.destination) setForm((f) => (f.dest ? f : { ...f, dest: data.destination }));

      if (data.alreadyDelivered) {
        // Read back what was actually submitted, so a re-track shows the real
        // record instead of a blank, disabled form. "Received At" (the location
        // text, e.g. "Main Gate") isn't included here — see note below handleSubmit;
        // it currently isn't sent to the server at all, so there's nothing to fetch.
        setForm((f) => ({
          ...f,
          status: "DELIVERED",
          receiver: data.receivedBy || "",
          remarks: data.remarks || "",
          time: data.deliveredOn || f.time,
        }));

        if (data.podImageUrl) {
          try {
            const imgRes = await api.get(data.podImageUrl, { responseType: "blob" });
            setPod({ file: null, url: URL.createObjectURL(imgRes.data) });
          } catch {
            // No POD photo to show — not fatal, the rest of the readback still stands.
          }
        }

        notify("This consignment is already delivered — no further update needed.", "bad");
      } else {
        notify("Consignment found", "ok");
      }
    } catch (err) {
      setTracked(null);
      notify(err.response?.data?.message || err.message || "Unable to fetch consignment", "bad");
    } finally {
      setTracking(false);
    }
  };

  /* ----- POD photo ----- */
  const clearPod = () => {
    setPod(null);
    if (podInputRef.current) podInputRef.current.value = "";
  };
  const handlePod = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!ALLOWED_POD_TYPES.includes(f.type)) {
      notify("Please select a JPG, PNG or WEBP image", "bad");
      e.target.value = "";
      return;
    }
    if (f.size > MAX_BYTES) {
      notify("Image must be under 5 MB", "bad");
      e.target.value = "";
      return;
    }
    setPod({ file: f, url: URL.createObjectURL(f) });
  };

  /* ----- Validation ----- */
  const validate = () => {
    const er = {};
    if (!form.dest.trim()) er.dest = "Delivery destination is required";
    if (!form.time) er.time = "Delivery time is required";
    else if (form.time > nowLocal()) er.time = "Time cannot be in the future";
    if (!form.status) er.status = "Please select a status";
    if (rules.receiptRequired && !form.receiver.trim()) er.receiver = "Receiver name is required";
    if (rules.receiptRequired && !form.receivedAt.trim()) er.receivedAt = "Received at is required";
    if (rules.showMobile && form.mobile && !/^[6-9]\d{9}$/.test(form.mobile))
      er.mobile = "Enter a valid 10-digit mobile number";
    if (rules.showPkgs && !(Number(form.pkgs) > 0)) er.pkgs = "Enter packages delivered";
    if (rules.showReason && !form.reason) er.reason = "Please select a reason";
    // Backend requires a POD photo for DELIVERED / PARTIAL — enforce it here too
    // so the driver isn't sent to the server before finding out it's missing.
    if (rules.showPod && !pod) er.pod = "Please capture or upload a delivery photo";

    setErrors(er);
    const first = Object.keys(er)[0];
    if (first) fieldRefs.current[first]?.scrollIntoView({ behavior: "smooth", block: "center" });
    return !first;
  };

  /* ----- Submit ----- */
  const resetForm = (all) => {
    setForm(EMPTY_FORM);
    setErrors({});
    clearPod();
    if (all) {
      setCnNo("");
      setCnInvalid(false);
      setTracked(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!tracked) {
      notify("Track a consignment first", "bad");
      return;
    }
    if (tracked.alreadyDelivered) {
      notify("This consignment is already delivered — no further update needed.", "bad");
      return;
    }
    if (!validate()) {
      notify("Please fill the required fields", "bad");
      return;
    }

    setSubmitting(true);
    try {
      let response;
      if (demo) {
        await sleep(800);
      } else {
        const [datePart, rawTime = "00:00"] = form.time.split("T");
        const timePart = rawTime.length === 5 ? `${rawTime}:00` : rawTime;
        const remarkText = [rules.showReason ? form.reason : "", form.remarks.trim()].filter(Boolean).join(" - ");

        // Same field names drs-delivery-update expects (InsertAppRequest), now sent as
        // multipart/form-data instead of JSON so the POD photo can travel in the same
        // request — a file can't go inside a JSON body without base64-encoding it.
        const fd = new FormData();
        fd.append("cons_no", tracked.cnNo);
        fd.append("consignee_name", tracked.consignee || "");
        fd.append("cons_delivery_date", datePart);
        fd.append("drs_time", `${datePart} ${timePart}`);
        fd.append("cons_status", STATUS_TO_DB[form.status] || "ATTEMPTED");
        fd.append("received_by", form.receiver.trim());
        fd.append("remarks", remarkText);
        fd.append("delivery_place", form.dest.trim());
        fd.append("booking_place", tracked.bookingPlace || "");
        fd.append("destination", form.dest.trim());
        fd.append("cons_status_id", "0");
        fd.append("guser_id", String(userId ?? ""));
        fd.append("company_code", String(tracked.companyCode ?? companyCode ?? ""));
        fd.append("longitude", "0");
        fd.append("latitude", "0");
        // Field name must be "PodImage" to match InsertAppRequest.PodImage on the server.
        if (rules.showPod && pod?.file) fd.append("PodImage", pod.file, pod.file.name);

        // IMPORTANT: don't let the shared axios instance's default
        // 'Content-Type: application/json' (or similar) header ride along on
        // this request. FormData needs the browser to set
        // 'multipart/form-data; boundary=...' itself — if a JSON content type
        // (or a fixed multipart type with no boundary) is forced instead, the
        // server can't parse any of the form fields, and every [FromForm]
        // property (cons_no, destination, time, status, ...) silently binds
        // to its default/empty value. That's what was producing
        // "Consignment number is required" even though cons_no was set here.
        const res = await api.post("/drs-delivery-update", fd, {
          headers: { "Content-Type": undefined },
        });
        response = res.data;
        if (!response?.success) {
          if (response?.code === 8) {
            // Someone else delivered it between this device's track and submit —
            // sync the UI so it locks instead of inviting another retry.
            setTracked((t) => (t ? { ...t, alreadyDelivered: true, status: "Delivered" } : t));
          }
          throw new Error(response?.body || response?.message || "Update failed");
        }
      }
      const label = statuses.find((s) => s.value === form.status)?.label || form.status;
      setTracked((t) => ({ ...t, status: label }));
      notify(`Consignment ${tracked.cnNo} updated: ${label}`, "ok");
      onUpdated?.(tracked.cnNo, label, response);
      resetForm(false);
    } catch (err) {
      notify(err.response?.data?.body || err.response?.data?.message || err.message || "Update failed, try again", "bad");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="dlv-root">
      <header className="dlv-appbar">
        <button type="button" aria-label="Back" onClick={onBack}>
          <BackIcon />
        </button>
        <h1>Delivery</h1>
      </header>

      <main className="dlv-main">
        {/* ===== Track consignment ===== */}
        <section className="dlv-card">
          <form className="dlv-track-row" onSubmit={handleTrack} autoComplete="off">
            <input
              className={`dlv-input ${cnInvalid ? "invalid" : ""}`}
              type="text"
              value={cnNo}
              onChange={(e) => {
                setCnNo(e.target.value.toUpperCase());
                setCnInvalid(false);
              }}
              placeholder="Consignment No."
              aria-label="Consignment number"
              autoCapitalize="characters"
            />
            <button className="dlv-btn dlv-btn-primary" type="submit" disabled={tracking}>
              {tracking ? <span className="dlv-spinner" /> : (<><SearchIcon /><span>Track</span></>)}
            </button>
          </form>

          <dl className="dlv-details">
            <Detail label="Consignor" value={tracked?.consignor} />
            <Detail label="Consignee" value={tracked?.consignee} />
            <Detail label="Con. Status" value={tracked?.status} badge />
            <Detail label="Date" value={tracked?.date} />
          </dl>

          {tracked?.alreadyDelivered && (
            <div
              className="dlv-delivered-banner"
              role="status"
              style={{
                marginTop: 12,
                padding: "10px 14px",
                borderRadius: 8,
                background: "#e8f6ed",
                color: "#1e7a3d",
                fontWeight: 600,
                fontSize: 14,
              }}
            >
              ✓ This consignment is already delivered. No further update is needed.
            </div>
          )}
        </section>

        {/* ===== Update consignment ===== */}
        <section className="dlv-card">
          <h2 className="dlv-section-title">Update Consignment</h2>

          <form
            id="dlv-update-form"
            className="dlv-form"
            onSubmit={handleSubmit}
            noValidate
            inert={tracked?.alreadyDelivered ? "" : undefined}
          >
            <Field name="dest" label="Delivery Dest." required full error={errors.dest} fieldRef={regField("dest")}>
              <input type="text" placeholder="Enter delivery destination" {...bind("dest")} />
            </Field>

            <Field name="time" label="Time" required error={errors.time} fieldRef={regField("time")}>
              <div className="dlv-time-wrap">
                <input type="datetime-local" max={nowLocal()} {...bind("time")} />
                <button className="dlv-btn dlv-btn-time" type="button" title="Set current time" onClick={() => setField("time", nowLocal())}>
                  <ClockIcon /> Now
                </button>
              </div>
            </Field>

            <Field name="status" label="Status" required error={errors.status} fieldRef={regField("status")}>
              <select id="dlv-status" className={`dlv-input ${errors.status ? "invalid" : ""}`} value={form.status} onChange={handleStatus}>
                <option value="">-Select-</option>
                {statuses.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </Field>

            <Field name="receiver" label="Receiver" required={rules.receiptRequired} error={errors.receiver} fieldRef={regField("receiver")}>
              <input type="text" placeholder="Receiver name" {...bind("receiver")} />
            </Field>

            <Field name="receivedAt" label="Received At" required={rules.receiptRequired} error={errors.receivedAt} fieldRef={regField("receivedAt")}>
              <input type="text" list="dlv-receivedAt-list" placeholder="e.g. Main Gate, Stores" {...bind("receivedAt")} />
              <datalist id="dlv-receivedAt-list">
                {receivedAtOptions.map((o) => (
                  <option key={o} value={o} />
                ))}
              </datalist>
            </Field>

            {rules.showMobile && (
              <Field name="mobile" label="Receiver Mobile" error={errors.mobile} fieldRef={regField("mobile")}>
                <input
                  type="tel"
                  inputMode="numeric"
                  maxLength={10}
                  placeholder="10-digit mobile"
                  {...bind("mobile")}
                  onChange={(e) => setField("mobile", e.target.value.replace(/\D/g, ""))}
                />
              </Field>
            )}

            {rules.showPkgs && (
              <Field name="pkgs" label="Packages Delivered" required error={errors.pkgs} fieldRef={regField("pkgs")}>
                <input type="number" inputMode="numeric" min="1" placeholder="No. of packages" {...bind("pkgs")} />
              </Field>
            )}

            {rules.showReason && (
              <Field name="reason" label="Reason" required full error={errors.reason} fieldRef={regField("reason")}>
                <select {...bind("reason")}>
                  <option value="">-Select-</option>
                  {reasons.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </Field>
            )}

            <Field name="remarks" label="Remarks" full fieldRef={regField("remarks")}>
              <textarea maxLength={250} placeholder="Any additional remarks" {...bind("remarks")} />
            </Field>

            {rules.showPod && (
              <Field name="pod" label="POD Photo" required={rules.showPod} full error={errors.pod} fieldRef={regField("pod")}>
                <input ref={podInputRef} type="file" accept="image/jpeg,image/png,image/webp" capture="environment" hidden onChange={handlePod} />
                <div
                  className="dlv-photo-box"
                  role="button"
                  tabIndex={0}
                  onClick={() => podInputRef.current?.click()}
                  onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && podInputRef.current?.click()}
                >
                  {pod ? (
                    <>
                      <img src={pod.url} alt="POD preview" />
                      <button
                        type="button"
                        className="dlv-remove"
                        aria-label="Remove photo"
                        onClick={(e) => {
                          e.stopPropagation();
                          clearPod();
                        }}
                      >
                        ✕
                      </button>
                    </>
                  ) : (
                    <>
                      <CameraIcon />
                      <span>Tap to capture / upload proof of delivery</span>
                    </>
                  )}
                </div>
              </Field>
            )}
          </form>
        </section>
      </main>

      <footer className="dlv-footer">
        <div className="dlv-footer-inner">
          <button className="dlv-btn dlv-btn-outline" type="button" onClick={() => resetForm(true)}>
            Clear
          </button>
          <button
            className="dlv-btn dlv-btn-primary"
            type="submit"
            form="dlv-update-form"
            disabled={submitting || tracked?.alreadyDelivered}
            title={tracked?.alreadyDelivered ? "Already delivered — nothing to update" : undefined}
          >
            {submitting ? (<><span className="dlv-spinner" /> Updating…</>) : "Update Consignment"}
          </button>
        </div>
      </footer>

      <div className={`dlv-toast ${toast.show ? "show" : ""} ${toast.type}`}>{toast.msg}</div>
    </div>
  );
}
